import React, { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { 
  ArrowLeft, 
  User, 
  Building,
  MapPin,
  Phone,
  Calendar,
  Camera,
  FileText,
  Send,
  Plus,
  X,
  Download,
  Navigation,
  Clock,
  CheckCircle,
  AlertCircle,
  Edit,
  Save,
  Image,
  Paperclip
} from 'lucide-react';
import { dossierDetailData } from '../../data/mockData';

// Données importées depuis mockData
const enqueteurDossierData = dossierDetailData;

const EnqueteurDossierDetail = () => {
  const { id } = useParams();
  const [activeTab, setActiveTab] = useState('informations');
  const [isAddingConstat, setIsAddingConstat] = useState(false);
  const [newConstat, setNewConstat] = useState({
    titre: '',
    description: '',
    observations: '',
    photos: []
  });
  const [showTerminerEnqueteModal, setShowTerminerEnqueteModal] = useState(false);
  
  const dossier = enqueteurDossierData[id];

  if (!dossier) {
    return (
      <div className="text-center py-12">
        <FileText className="h-12 w-12 text-port-gray-400 mx-auto mb-4" />
        <h3 className="text-lg font-medium text-port-gray-900 mb-2">Dossier non trouvé</h3>
        <Link to="/enqueteur" className="btn btn-primary">
          <ArrowLeft className="h-4 w-4 mr-2" />
          Retour au tableau de bord
        </Link>
      </div>
    );
  }

  const handlePhotoAdd = () => {
    // Simulation d'ajout de photo
    const newPhoto = {
      id: Date.now(),
      nom: `photo_${Date.now()}.jpg`,
      commentaire: ''
    };
    setNewConstat(prev => ({
      ...prev,
      photos: [...prev.photos, newPhoto]
    }));
  };

  const handlePhotoRemove = (photoId) => {
    setNewConstat(prev => ({
      ...prev,
      photos: prev.photos.filter(p => p.id !== photoId)
    }));
  };

  const handlePhotoCommentChange = (photoId, commentaire) => {
    setNewConstat(prev => ({
      ...prev,
      photos: prev.photos.map(p => p.id === photoId ? { ...p, commentaire } : p)
    }));
  };

  const handleConstatSubmit = (e) => {
    e.preventDefault();
    if (newConstat.titre && newConstat.description) {
      // Ici on enverrait le constat via API
      console.log('Nouveau suivi :', newConstat);
      setIsAddingConstat(false);
      setNewConstat({ titre: '', description: '', observations: '', photos: [] });
      alert('Suivi ajouté avec succès !');
    }
  };

  const handleTerminerEnquete = () => {
    // Marquer le dossier comme terminé
    console.log('Dossier marqué comme terminé:', dossier.reference);
    // Ici on enverrait la demande au backend pour :
    // - Changer le statut à "terminé"
    // - Générer le rapport de mission
    // - Envoyer un mail à Provelliance
    setShowTerminerEnqueteModal(false);
    alert('Dossier marqué comme terminé. Le rapport de mission est en cours de génération.');
  };

  const getStatutBadge = (statut) => {
    const badges = {
      'en_attente_acceptation': { class: 'badge-info', label: 'Attribué', icon: Clock },
      'en_cours': { class: 'badge-warning', label: 'En cours', icon: Clock },
      'cloture': { class: 'badge-success', label: 'Terminé', icon: CheckCircle }
    };
    return badges[statut] || { class: 'badge-secondary', label: statut, icon: AlertCircle };
  };

  const statutBadge = getStatutBadge(dossier.statut);
  const StatutIcon = statutBadge.icon;

  return (
    <div className="animate-slide-in">
      {/* Header Mobile-First */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center">
          <Link 
            to="/enqueteur" 
            className="p-2 text-port-gray-600 hover:bg-port-gray-100 rounded-lg transition-colors mr-3"
          >
            <ArrowLeft className="h-5 w-5" />
          </Link>
          <div>
            <h1 className="text-lg md:text-xl font-bold text-port-gray-900">
              {dossier.reference}
            </h1>
            <div className="flex items-center mt-1">
              <span className={`badge ${statutBadge.class} text-xs mr-2`}>
                <StatutIcon className="h-3 w-3 mr-1" />
                {statutBadge.label}
              </span>
              <span className="text-xs text-port-gray-500">
                {dossier.sinistre.type}
              </span>
            </div>
          </div>
        </div>

        {dossier.statut !== 'cloture' && (
          <button
            onClick={() => setShowTerminerEnqueteModal(true)}
            className="btn btn-primary btn-sm"
          >
            <CheckCircle className="h-4 w-4 mr-1" />
            Terminer l'enquête
          </button>
        )}
      </div>

      {/* Modal confirmation terminer l'enquête */}
      {showTerminerEnqueteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold text-port-gray-900 mb-4">
              Terminer l'enquête
            </h3>
            <p className="text-port-gray-700 mb-6">
              Vous allez marquer ce dossier comme terminé, cela génèrera le rapport de mission que vous pourrez télécharger et avertira Provelliance par mail.
            </p>
            <div className="flex space-x-3">
              <button
                onClick={() => setShowTerminerEnqueteModal(false)}
                className="flex-1 px-4 py-2 border border-port-gray-300 text-port-gray-700 rounded-lg hover:bg-port-gray-50 transition-colors font-medium"
              >
                Annuler
              </button>
              <button
                onClick={handleTerminerEnquete}
                className="flex-1 px-4 py-2 bg-atl-green-600 text-white rounded-lg hover:bg-atl-green-700 transition-colors font-medium"
              >
                Confirmer
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Onglets */}
      <div className="border-b border-port-gray-200 mb-4">
        <nav className="-mb-px flex space-x-6 overflow-x-auto">
          <button
            onClick={() => setActiveTab('informations')}
            className={`py-2 px-1 border-b-2 font-medium text-sm whitespace-nowrap ${
              activeTab === 'informations'
                ? 'border-atl-blue-500 text-atl-blue-600'
                : 'border-transparent text-port-gray-500'
            }`}
          >
            Informations
          </button>
          <button
            onClick={() => setActiveTab('constats')}
            className={`py-2 px-1 border-b-2 font-medium text-sm whitespace-nowrap ${
              activeTab === 'constats'
                ? 'border-atl-blue-500 text-atl-blue-600'
                : 'border-transparent text-port-gray-500'
            }`}
          >
            Suivi d'activité ({dossier.constats.length})
          </button>
          <button
            onClick={() => setActiveTab('documents')}
            className={`py-2 px-1 border-b-2 font-medium text-sm whitespace-nowrap ${
              activeTab === 'documents'
                ? 'border-atl-blue-500 text-atl-blue-600'
                : 'border-transparent text-port-gray-500'
            }`}
          >
            Documents
          </button>
        </nav>
      </div>

      {/* Contenu des onglets */}
      {activeTab === 'informations' && (
        <div className="space-y-4">
          {/* Informations sur le sinistre */}
          <div className="bg-white rounded-lg p-4 shadow-sm border border-port-gray-200">
            <h3 className="text-lg font-medium mb-3 flex items-center">
              <AlertCircle className="h-5 w-5 mr-2 text-red-600" />
              Détails du Sinistre
            </h3>
            <div className="space-y-3">
              <div>
                <label className="text-sm font-medium text-port-gray-600">Type / Nature</label>
                <p className="text-port-gray-900">{dossier.sinistre.type} - {dossier.sinistre.nature}</p>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-port-gray-600">Date</label>
                  <p className="text-port-gray-900">
                    {new Date(dossier.sinistre.date).toLocaleDateString('fr-FR')}
                  </p>
                </div>
                <div>
                  <label className="text-sm font-medium text-port-gray-600">Heure</label>
                  <p className="text-port-gray-900">{dossier.sinistre.heure}</p>
                </div>
              </div>

              <div>
                <label className="text-sm font-medium text-port-gray-600 flex items-center">
                  <MapPin className="h-4 w-4 mr-1" />
                  Lieu
                </label>
                <div className="flex items-center justify-between">
                  <p className="text-port-gray-900 flex-1">{dossier.sinistre.lieu}</p>
                  <a
                    href={`https://maps.google.com/?q=${encodeURIComponent(dossier.sinistre.lieu)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="p-2 text-atl-blue-600 hover:bg-atl-blue-100 rounded-lg transition-colors"
                  >
                    <Navigation className="h-4 w-4" />
                  </a>
                </div>
              </div>

              <div>
                <label className="text-sm font-medium text-port-gray-600">Description</label>
                <p className="text-port-gray-900 text-sm leading-relaxed">{dossier.sinistre.description}</p>
              </div>

              <div>
                <label className="text-sm font-medium text-port-gray-600">Montant estimé</label>
                <p className="text-xl font-bold text-atl-blue-600">
                  {dossier.montantEstime.toLocaleString('fr-FR')} €
                </p>
              </div>
            </div>
          </div>

          {/* Informations assuré */}
          <div className="bg-white rounded-lg p-4 shadow-sm border border-port-gray-200">
            <h3 className="text-lg font-medium mb-3 flex items-center">
              <User className="h-5 w-5 mr-2 text-atl-blue-600" />
              Assuré
            </h3>
            <div className="space-y-3">
              <div>
                <label className="text-sm font-medium text-port-gray-600">Nom complet</label>
                <p className="text-port-gray-900 font-medium">
                  {dossier.assure.prenom} {dossier.assure.nom}
                </p>
              </div>

              <div>
                <label className="text-sm font-medium text-port-gray-600">Adresse</label>
                <p className="text-port-gray-900">{dossier.assure.adresse}</p>
              </div>

              <div className="flex space-x-4">
                <a
                  href={`tel:${dossier.assure.telephone}`}
                  className="flex items-center px-4 py-2 bg-atl-green-100 text-atl-green-700 rounded-lg hover:bg-atl-green-200 transition-colors"
                >
                  <Phone className="h-4 w-4 mr-2" />
                  <span className="text-sm">{dossier.assure.telephone}</span>
                </a>
                
                {dossier.assure.email && (
                  <a
                    href={`mailto:${dossier.assure.email}`}
                    className="flex items-center px-4 py-2 bg-atl-blue-100 text-atl-blue-700 rounded-lg hover:bg-atl-blue-200 transition-colors"
                  >
                    <span className="text-sm">{dossier.assure.email}</span>
                  </a>
                )}
              </div>
            </div>
          </div>

          {/* Informations client */}
          <div className="bg-white rounded-lg p-4 shadow-sm border border-port-gray-200">
            <h3 className="text-lg font-medium mb-3 flex items-center">
              <Building className="h-5 w-5 mr-2 text-port-gray-600" />
              Client (Compagnie)
            </h3>
            <div className="space-y-3">
              <div>
                <label className="text-sm font-medium text-port-gray-600">Compagnie</label>
                <p className="text-port-gray-900 font-medium">{dossier.client.nom}</p>
              </div>

              <div>
                <label className="text-sm font-medium text-port-gray-600">Contact</label>
                <p className="text-port-gray-900">{dossier.client.contact}</p>
              </div>

              <a
                href={`tel:${dossier.client.telephone}`}
                className="flex items-center px-4 py-2 bg-port-gray-100 text-port-gray-700 rounded-lg hover:bg-port-gray-200 transition-colors w-fit"
              >
                <Phone className="h-4 w-4 mr-2" />
                <span className="text-sm">{dossier.client.telephone}</span>
              </a>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'constats' && (
        <div className="space-y-4">
          {/* Bouton ajouter constat */}
          <div className="flex justify-between items-center">
            <h3 className="text-lg font-medium">Mon suivi d'activité</h3>
            <button
              onClick={() => setIsAddingConstat(true)}
              className="btn btn-primary btn-sm"
            >
              <Plus className="h-4 w-4 mr-2" />
              Ajouter
            </button>
          </div>

          {/* Formulaire nouveau constat */}
          {isAddingConstat && (
            <div className="bg-white rounded-lg p-4 shadow-sm border border-port-gray-200">
              <form onSubmit={handleConstatSubmit}>
                <h4 className="font-medium mb-4">Nouveau Suivi</h4>
                
                <div className="space-y-4">
                  <div>
                    <label className="form-label">Titre *</label>
                    <input
                      type="text"
                      value={newConstat.titre}
                      onChange={(e) => setNewConstat(prev => ({ ...prev, titre: e.target.value }))}
                      placeholder="Ex: Visite d'expertise, Suivi travaux..."
                      className="form-input"
                      required
                    />
                  </div>

                  <div>
                    <label className="form-label">Description *</label>
                    <textarea
                      value={newConstat.description}
                      onChange={(e) => setNewConstat(prev => ({ ...prev, description: e.target.value }))}
                      placeholder="Description de l'intervention..."
                      className="form-textarea"
                      rows="3"
                      required
                    />
                  </div>

                  <div>
                    <label className="form-label">Observations</label>
                    <textarea
                      value={newConstat.observations}
                      onChange={(e) => setNewConstat(prev => ({ ...prev, observations: e.target.value }))}
                      placeholder="Observations et recommandations..."
                      className="form-textarea"
                      rows="3"
                    />
                  </div>

                  {/* Photos */}
                  <div>
                    <div className="flex justify-between items-center mb-3">
                      <label className="form-label">Photos</label>
                      <button
                        type="button"
                        onClick={handlePhotoAdd}
                        className="btn btn-secondary btn-sm"
                      >
                        <Camera className="h-4 w-4 mr-2" />
                        Ajouter Photo
                      </button>
                    </div>

                    {newConstat.photos.length > 0 && (
                      <div className="space-y-3">
                        {newConstat.photos.map(photo => (
                          <div key={photo.id} className="flex items-center p-3 border border-port-gray-200 rounded-lg">
                            <Image className="h-8 w-8 text-port-gray-400 mr-3" />
                            <div className="flex-1">
                              <p className="text-sm font-medium text-port-gray-900">{photo.nom}</p>
                              <input
                                type="text"
                                placeholder="Commentaire sur la photo..."
                                value={photo.commentaire}
                                onChange={(e) => handlePhotoCommentChange(photo.id, e.target.value)}
                                className="form-input mt-1 text-sm"
                              />
                            </div>
                            <button
                              type="button"
                              onClick={() => handlePhotoRemove(photo.id)}
                              className="p-2 text-red-600 hover:bg-red-100 rounded-lg transition-colors"
                            >
                              <X className="h-4 w-4" />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                <div className="flex space-x-3 mt-6">
                  <button
                    type="button"
                    onClick={() => {
                      setIsAddingConstat(false);
                      setNewConstat({ titre: '', description: '', observations: '', photos: [] });
                    }}
                    className="btn btn-secondary flex-1"
                  >
                    Annuler
                  </button>
                  <button type="submit" className="btn btn-primary flex-1">
                    <Save className="h-4 w-4 mr-2" />
                    Enregistrer
                  </button>
                </div>
              </form>
            </div>
          )}

          {/* Liste des constats */}
          <div className="space-y-4">
            {dossier.constats.map(constat => (
              <div key={constat.id} className="bg-white rounded-lg p-4 shadow-sm border border-port-gray-200">
                <div className="flex justify-between items-start mb-3">
                  <div>
                    <h4 className="font-medium text-port-gray-900">{constat.titre}</h4>
                    <p className="text-sm text-port-gray-500 flex items-center mt-1">
                      <Calendar className="h-4 w-4 mr-1" />
                      {new Date(constat.date).toLocaleString('fr-FR')}
                    </p>
                  </div>
                  <span className={`badge ${
                    constat.type === 'visite_initiale' ? 'badge-info' : 'badge-secondary'
                  } text-xs`}>
                    {constat.type === 'visite_initiale' ? 'Visite initiale' : 'Suivi'}
                  </span>
                </div>

                <div className="space-y-3">
                  <div>
                    <label className="text-sm font-medium text-port-gray-600">Description</label>
                    <p className="text-sm text-port-gray-900">{constat.description}</p>
                  </div>

                  {constat.observations && (
                    <div>
                      <label className="text-sm font-medium text-port-gray-600">Observations</label>
                      <p className="text-sm text-port-gray-900">{constat.observations}</p>
                    </div>
                  )}

                  {constat.photos.length > 0 && (
                    <div>
                      <label className="text-sm font-medium text-port-gray-600 mb-2 block">
                        Photos ({constat.photos.length})
                      </label>
                      <div className="grid grid-cols-1 gap-2">
                        {constat.photos.map(photo => (
                          <div key={photo.id} className="flex items-center p-2 bg-port-gray-50 rounded-lg">
                            <Image className="h-6 w-6 text-port-gray-400 mr-2" />
                            <div className="flex-1">
                              <p className="text-sm font-medium text-port-gray-900">{photo.nom}</p>
                              {photo.commentaire && (
                                <p className="text-xs text-port-gray-600">{photo.commentaire}</p>
                              )}
                            </div>
                            <button className="p-1 text-atl-blue-600 hover:bg-atl-blue-100 rounded">
                              <Download className="h-4 w-4" />
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>

          {dossier.constats.length === 0 && !isAddingConstat && (
            <div className="text-center py-12 bg-white rounded-lg border border-port-gray-200">
              <FileText className="h-12 w-12 text-port-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-port-gray-900 mb-2">
                Aucun suivi ajouté
              </h3>
              <p className="text-port-gray-500 mb-4">
                Commencez par ajouter votre premier suivi d'activité.
              </p>
              <button
                onClick={() => setIsAddingConstat(true)}
                className="btn btn-primary"
              >
                <Plus className="h-4 w-4 mr-2" />
                Ajouter un suivi
              </button>
            </div>
          )}
        </div>
      )}

      {activeTab === 'documents' && (
        <div className="space-y-4">
          <h3 className="text-lg font-medium">Documents du dossier</h3>
          
          <div className="space-y-3">
            {dossier.documentsTransmis.map(document => (
              <div key={document.id} className="bg-white rounded-lg p-4 shadow-sm border border-port-gray-200">
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <div className="p-2 bg-atl-blue-100 rounded-lg mr-3">
                      <Paperclip className="h-5 w-5 text-atl-blue-600" />
                    </div>
                    <div>
                      <h4 className="font-medium text-port-gray-900">{document.nom}</h4>
                      <p className="text-sm text-port-gray-500">{document.type}</p>
                    </div>
                  </div>
                  
                  <button className="btn btn-secondary btn-sm">
                    <Download className="h-4 w-4 mr-2" />
                    Télécharger
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Actions flottantes pour mobile */}
      <div className="fixed bottom-6 right-6 md:hidden">
        <div className="flex flex-col space-y-3">
          <button
            onClick={() => setIsAddingConstat(true)}
            className="w-12 h-12 bg-atl-green-600 text-white rounded-full shadow-lg flex items-center justify-center"
          >
            <Camera className="h-5 w-5" />
          </button>
          {dossier.statut !== 'cloture' && (
            <button
              onClick={() => setShowTerminerEnqueteModal(true)}
              className="w-12 h-12 bg-atl-green-600 text-white rounded-full shadow-lg flex items-center justify-center"
            >
              <CheckCircle className="h-5 w-5" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default EnqueteurDossierDetail;
